import { useState } from 'react';
import { MAX_UPSTREAM_SKU_COST_MULTIPLIER } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  FormField,
  Input,
  Switch,
} from '../../ui/index.ts';
import { coerceErrorMessage } from '../Toast.tsx';
import {
  WEEKDAY_BITS,
  WEEKDAY_KEYS,
  bitOn,
  draftCrossesMidnight,
  draftProblems,
  emptyWindowDraft,
  toggleBit,
  type DraftProblem,
  type DraftProblemField,
  type DraftProblemReason,
  type WindowDraft,
  type WeekdayKey,
} from './scheduleWindowDraft.ts';

export interface ScheduleWindowsCopy {
  readonly none: I18nKey;
  readonly active: I18nKey;
  readonly base: I18nKey;
  readonly effective: I18nKey;
  readonly configure: I18nKey;
  readonly dialogTitle: I18nKey;
  readonly dialogDescription: I18nKey;
  readonly close: I18nKey;
  readonly cancel: I18nKey;
  readonly save: I18nKey;
  readonly add: I18nKey;
  readonly remove: I18nKey;
  readonly empty: I18nKey;
  readonly enabled: I18nKey;
  readonly label: I18nKey;
  readonly timezone: I18nKey;
  readonly days: I18nKey;
  readonly day: Record<WeekdayKey, I18nKey>;
  readonly start: I18nKey;
  readonly end: I18nKey;
  readonly crossMidnight: I18nKey;
  readonly multiplier: I18nKey;
  readonly labelRequired: I18nKey;
  readonly timezoneRequired: I18nKey;
  readonly daysRequired: I18nKey;
  readonly clockInvalid: I18nKey;
  readonly startEndEqual: I18nKey;
  readonly startNotEndOfDay: I18nKey;
  readonly multiplierInvalid: I18nKey;
  readonly advanced?: I18nKey;
  readonly input?: I18nKey;
  readonly output?: I18nKey;
  readonly cacheRead?: I18nKey;
  readonly cacheWrite?: I18nKey;
  readonly reasoning?: I18nKey;
  readonly needPrice?: I18nKey;
}

const DIMENSION_FIELDS = [
  ['inputCostMultiplier', 'input'],
  ['outputCostMultiplier', 'output'],
  ['cacheReadCostMultiplier', 'cacheRead'],
  ['cacheWriteCostMultiplier', 'cacheWrite'],
  ['reasoningCostMultiplier', 'reasoning'],
] as const;

function patchDraft(draft: WindowDraft, patch: Partial<WindowDraft>): WindowDraft {
  return { ...draft, ...patch };
}

function problemOn(
  problems: readonly DraftProblem[],
  field: DraftProblemField,
): DraftProblem | undefined {
  return problems.find((problem) => problem.field === field);
}

export function ScheduleWindowsEditor({
  testIdPrefix,
  testIdWindowPrefix,
  subjectKey,
  titleParams,
  copy,
  baseMultiplier,
  effectiveMultiplier,
  activeLabel,
  loaded,
  available,
  saving,
  loadDrafts,
  parseDrafts,
  onSave,
  showDimensions,
}: {
  readonly testIdPrefix: string;
  readonly testIdWindowPrefix: string;
  readonly subjectKey: string;
  readonly titleParams: { readonly model: string };
  readonly copy: ScheduleWindowsCopy;
  readonly baseMultiplier: number;
  readonly effectiveMultiplier: number;
  readonly activeLabel: string | undefined;
  readonly loaded: boolean;
  readonly available: boolean;
  readonly saving: boolean;
  readonly loadDrafts: () => readonly WindowDraft[];
  readonly parseDrafts: (drafts: readonly WindowDraft[]) => readonly unknown[] | null;
  readonly onSave: (drafts: readonly WindowDraft[]) => Promise<void>;
  readonly showDimensions: boolean;
}): JSX.Element {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState<readonly WindowDraft[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [draftCounter, setDraftCounter] = useState(0);

  const problemKey: Record<DraftProblemReason, I18nKey> = {
    label_required: copy.labelRequired,
    timezone_required: copy.timezoneRequired,
    days_required: copy.daysRequired,
    clock_invalid: copy.clockInvalid,
    start_end_equal: copy.startEndEqual,
    start_not_end_of_day: copy.startNotEndOfDay,
    multiplier_invalid: copy.multiplierInvalid,
  };

  function openEditor(): void {
    if (!available) return;
    const loadedDrafts = loadDrafts();
    setDrafts(loadedDrafts);
    setDraftCounter(loadedDrafts.length);
    setServerError(null);
    setOpen(true);
  }

  function updateDraft(key: string, patch: Partial<WindowDraft>): void {
    setDrafts((current) => current.map((draft) => (draft.key === key ? patchDraft(draft, patch) : draft)));
  }

  function addWindow(): void {
    const next = draftCounter + 1;
    setDraftCounter(next);
    setDrafts((current) => [...current, emptyWindowDraft(`new-${next}`)]);
  }

  function removeWindow(key: string): void {
    setDrafts((current) => current.filter((draft) => draft.key !== key));
  }

  async function handleSave(): Promise<void> {
    if (parseDrafts(drafts) === null) return;
    setServerError(null);
    try {
      await onSave(drafts);
      setOpen(false);
    } catch (err) {
      setServerError(coerceErrorMessage(err));
    }
  }

  const canSave = available && parseDrafts(drafts) !== null && !saving;
  const multiplierMax = String(MAX_UPSTREAM_SKU_COST_MULTIPLIER);

  return (
    <div className="flex flex-col items-start gap-1.5" data-testid={`${testIdPrefix}-${subjectKey}`}>
      <div className="flex flex-wrap items-center gap-2">
        {loaded && available ? (
          <>
            <Chip tone="neutral">
              {t(copy.base, { value: baseMultiplier })}
            </Chip>
            <Chip tone={activeLabel === undefined ? 'neutral' : 'ok'}>
              {t(copy.effective, { value: effectiveMultiplier })}
            </Chip>
            <Chip tone={activeLabel === undefined ? 'neutral' : 'ok'}>
              {activeLabel === undefined
                ? t(copy.none)
                : t(copy.active, { label: activeLabel })}
            </Chip>
          </>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          onClick={openEditor}
          disabled={!loaded || !available || saving}
          data-testid={`${testIdPrefix}-open-${subjectKey}`}
        >
          {t(copy.configure)}
        </Button>
      </div>
      {loaded && !available && copy.needPrice !== undefined ? (
        <span className="text-micro text-paper-low">{t(copy.needPrice)}</span>
      ) : null}

      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t(copy.dialogTitle, titleParams)}
        description={t(copy.dialogDescription)}
        closeLabel={t(copy.close)}
        size="lg"
        testId={`${testIdPrefix}-dialog-${subjectKey}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t(copy.cancel)}
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={!canSave}
              loading={saving}
              data-testid={`${testIdPrefix}-save-${subjectKey}`}
            >
              {t(copy.save)}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {drafts.length === 0 ? (
            <p className="m-0 text-body text-paper-mid" data-testid={`${testIdPrefix}-empty`}>
              {t(copy.empty)}
            </p>
          ) : (
            drafts.map((draft, index) => (
              <WindowDraftCard
                key={draft.key}
                draft={draft}
                index={index}
                subjectKey={subjectKey}
                testIdWindowPrefix={testIdWindowPrefix}
                copy={copy}
                problemKey={problemKey}
                showDimensions={showDimensions}
                multiplierMax={multiplierMax}
                onChange={(patch) => updateDraft(draft.key, patch)}
                onRemove={() => removeWindow(draft.key)}
              />
            ))
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={addWindow}
            data-testid={`${testIdPrefix}-add-${subjectKey}`}
          >
            {t(copy.add)}
          </Button>
          {serverError !== null ? (
            <p className="m-0 text-label text-bad" role="alert" data-testid={`${testIdPrefix}-error`}>
              {serverError}
            </p>
          ) : null}
        </div>
      </Dialog>
    </div>
  );
}

function WindowDraftCard({
  draft,
  index,
  subjectKey,
  testIdWindowPrefix,
  copy,
  problemKey,
  showDimensions,
  multiplierMax,
  onChange,
  onRemove,
}: {
  readonly draft: WindowDraft;
  readonly index: number;
  readonly subjectKey: string;
  readonly testIdWindowPrefix: string;
  readonly copy: ScheduleWindowsCopy;
  readonly problemKey: Record<DraftProblemReason, I18nKey>;
  readonly showDimensions: boolean;
  readonly multiplierMax: string;
  readonly onChange: (patch: Partial<WindowDraft>) => void;
  readonly onRemove: () => void;
}): JSX.Element {
  const t = useT();
  const prefix = `${subjectKey}-${index}`;
  const problems = draftProblems(draft);
  const startProblem = problemOn(problems, 'start');
  const endProblem = problemOn(problems, 'end');
  const labelProblem = problemOn(problems, 'label');
  const timezoneProblem = problemOn(problems, 'timezone');
  const daysProblem = problemOn(problems, 'days');
  const multiplierProblem = problemOn(problems, 'costMultiplier');
  const dimensionProblems = showDimensions
    ? DIMENSION_FIELDS.filter(([field]) => problemOn(problems, field) !== undefined)
    : [];
  const crossesMidnight = draftCrossesMidnight(draft);

  function fieldError(problem: DraftProblem | undefined): string | undefined {
    if (problem === undefined) return undefined;
    return t(problemKey[problem.reason], { max: multiplierMax });
  }

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border border-line-soft bg-card p-3"
      data-testid={`${testIdWindowPrefix}-card-${prefix}`}
    >
      <div className="flex items-center justify-between gap-2">
        <Switch
          checked={draft.enabled}
          onCheckedChange={(checked) => onChange({ enabled: checked })}
          label={t(copy.enabled)}
          testId={`${testIdWindowPrefix}-enabled-${prefix}`}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          data-testid={`${testIdWindowPrefix}-remove-${prefix}`}
        >
          {t(copy.remove)}
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <FormField
          label={t(copy.label)}
          fieldId={`${prefix}-label`}
          required
          error={fieldError(labelProblem)}
        >
          <Input
            id={`${prefix}-label`}
            value={draft.label}
            invalid={labelProblem !== undefined}
            aria-describedby={labelProblem !== undefined ? `${prefix}-label-error` : undefined}
            onChange={(event) => onChange({ label: event.currentTarget.value })}
            data-testid={`${testIdWindowPrefix}-label-${prefix}`}
          />
        </FormField>
        <FormField
          label={t(copy.timezone)}
          fieldId={`${prefix}-timezone`}
          required
          error={fieldError(timezoneProblem)}
        >
          <Input
            id={`${prefix}-timezone`}
            value={draft.timezone}
            invalid={timezoneProblem !== undefined}
            aria-describedby={timezoneProblem !== undefined ? `${prefix}-timezone-error` : undefined}
            onChange={(event) => onChange({ timezone: event.currentTarget.value })}
            mono
            data-testid={`${testIdWindowPrefix}-timezone-${prefix}`}
          />
        </FormField>
      </div>
      <fieldset className="m-0 border-0 p-0">
        <legend className="mb-1.5 text-label text-paper-mid">
          {t(copy.days)}
        </legend>
        <div className="flex flex-wrap gap-3">
          {WEEKDAY_BITS.map((bit, dayIndex) => {
            const key = WEEKDAY_KEYS[dayIndex];
            if (key === undefined) return null;
            return (
              <Checkbox
                key={key}
                checked={bitOn(draft.daysOfWeek, bit)}
                onCheckedChange={() => onChange({ daysOfWeek: toggleBit(draft.daysOfWeek, bit) })}
                label={t(copy.day[key])}
                testId={`${testIdWindowPrefix}-day-${key}-${prefix}`}
              />
            );
          })}
        </div>
        {daysProblem !== undefined ? (
          <p className="mt-1.5 m-0 text-label text-bad" role="alert">
            {t(problemKey[daysProblem.reason])}
          </p>
        ) : null}
      </fieldset>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <FormField
          label={t(copy.start)}
          fieldId={`${prefix}-start`}
          required
          error={fieldError(startProblem)}
        >
          <Input
            id={`${prefix}-start`}
            value={draft.startClock}
            invalid={startProblem !== undefined}
            aria-describedby={startProblem !== undefined ? `${prefix}-start-error` : undefined}
            onChange={(event) => onChange({ startClock: event.currentTarget.value })}
            placeholder="09:00"
            mono
            data-testid={`${testIdWindowPrefix}-start-${prefix}`}
          />
        </FormField>
        <FormField
          label={t(copy.end)}
          fieldId={`${prefix}-end`}
          required
          error={fieldError(endProblem)}
        >
          <Input
            id={`${prefix}-end`}
            value={draft.endClock}
            invalid={endProblem !== undefined}
            aria-describedby={endProblem !== undefined ? `${prefix}-end-error` : undefined}
            onChange={(event) => onChange({ endClock: event.currentTarget.value })}
            placeholder="18:00"
            mono
            data-testid={`${testIdWindowPrefix}-end-${prefix}`}
          />
        </FormField>
        <FormField
          label={t(copy.multiplier)}
          fieldId={`${prefix}-multiplier`}
          required
          error={fieldError(multiplierProblem)}
        >
          <Input
            id={`${prefix}-multiplier`}
            type="number"
            min="0"
            step="any"
            value={draft.costMultiplier}
            invalid={multiplierProblem !== undefined}
            aria-describedby={multiplierProblem !== undefined ? `${prefix}-multiplier-error` : undefined}
            onChange={(event) => onChange({ costMultiplier: event.currentTarget.value })}
            mono
            data-testid={`${testIdWindowPrefix}-multiplier-${prefix}`}
          />
        </FormField>
      </div>
      {crossesMidnight ? (
        <p className="m-0 text-label text-paper-mid" data-testid={`${testIdWindowPrefix}-cross-midnight-${prefix}`}>
          {t(copy.crossMidnight)}
        </p>
      ) : null}
      {showDimensions && copy.advanced !== undefined ? (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange({ advancedOpen: !draft.advancedOpen })}
            data-testid={`${testIdWindowPrefix}-advanced-${prefix}`}
          >
            {t(copy.advanced)}
          </Button>
          {dimensionProblems.length > 0 && !draft.advancedOpen ? (
            <p className="m-0 text-label text-bad" role="alert">
              {t(copy.multiplierInvalid, { max: multiplierMax })}
            </p>
          ) : null}
          {draft.advancedOpen ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {DIMENSION_FIELDS.map(([field, copyKey]) => {
                const labelKey = copy[copyKey];
                if (labelKey === undefined) return null;
                const problem = problemOn(problems, field);
                return (
                  <FormField
                    key={field}
                    label={t(labelKey)}
                    fieldId={`${prefix}-${field}`}
                    error={fieldError(problem)}
                  >
                    <Input
                      id={`${prefix}-${field}`}
                      type="number"
                      min="0"
                      step="any"
                      value={draft[field]}
                      invalid={problem !== undefined}
                      aria-describedby={problem !== undefined ? `${prefix}-${field}-error` : undefined}
                      onChange={(event) => onChange({ [field]: event.currentTarget.value })}
                      mono
                    />
                  </FormField>
                );
              })}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
